/**
 * Fixed-boundary, time-of-day greeting lookup.
 *
 *   morning   5:00 – 11:59    Good morning
 *   afternoon 12:00 – 16:59   Good afternoon
 *   evening   17:00 – 20:59   Good evening
 *   night     21:00 – 04:59   Hey night owl   (wraps midnight)
 *
 * Clarification 2026-04-10 (FR-050): the browser already knows the user's
 * local time — no backend timezone logic required. This is a pure, testable
 * function keyed off `now.getHours()`.
 */

export type Period = 'morning' | 'afternoon' | 'evening' | 'night';

export interface Greeting {
  period: Period;
  phrase: string;
  subtitle: string;
}

const PHRASES: Record<Period, string> = {
  morning: 'Good morning',
  afternoon: 'Good afternoon',
  evening: 'Good evening',
  night: 'Hey night owl',
};

const SUBTITLES: Record<Period, readonly string[]> = {
  morning: [
    'Four apps waiting. The waves might be firing.',
    "Your coffee's brewing, so is the data.",
    'Fresh day, fresh tides.',
    'Morning light over the Pacific.',
  ],
  afternoon: [
    'Midday check-in.',
    'The markets are listening.',
    'Still time to catch a session.',
    'The afternoon window is open.',
  ],
  evening: [
    'Golden hour.',
    'Tonight looks promising.',
    'Sunset mode engaged.',
    'The front page is done updating.',
  ],
  night: [
    'The quiet hours.',
    'Charts never sleep.',
    'Moonlight over Malibu.',
    'Most of the day is behind us.',
  ],
} as const;

function pickPeriod(hour: number): Period {
  if (hour >= 5 && hour < 12) return 'morning';
  if (hour >= 12 && hour < 17) return 'afternoon';
  if (hour >= 17 && hour < 21) return 'evening';
  return 'night';
}

export function getGreeting(now: Date = new Date()): Greeting {
  const period = pickPeriod(now.getHours());
  const subs = SUBTITLES[period];
  const subtitle = subs[Math.floor(Math.random() * subs.length)];
  return { period, phrase: PHRASES[period], subtitle };
}
